import { GraphqlError } from "$lib/graphql/client";
import {
  errorMessage as rawErrorMessage,
  graphqlErrorCode,
  graphqlErrorDetails,
  graphqlErrorStatus,
  isForbidden,
  isInsufficientCredits,
  isConflict,
  isNotFound,
  isOptimisticLock,
  isTotpRequired,
  isUnauthorized,
  isValidationError,
  isWorkflowLocked,
} from "$lib/api/errors";
import { toastApiError } from "$lib/api/toastApiError";
import { userErrorMessage } from "$lib/api/userError";


export type LoadStatus = "idle" | "loading" | "ready" | "error";

/** User-facing error text (localized code fallbacks + field details). */
export const errorMessage = userErrorMessage;

export {
  rawErrorMessage,
  graphqlErrorCode,
  graphqlErrorDetails,
  graphqlErrorStatus,
  isForbidden,
  isInsufficientCredits,
  isConflict,
  isNotFound,
  isOptimisticLock,
  isTotpRequired,
  isUnauthorized,
  isValidationError,
  isWorkflowLocked,
  toastApiError,
  userErrorMessage,
};

export function isUnknownGraphqlField(err: unknown): boolean {
  if (!(err instanceof GraphqlError)) return false;
  return err.errors.some((e) =>
    /cannot query field|unknown field|is not defined by type|wasn't found in type/i.test(e.message),
  );
}

/**
 * Lightweight async resource for SPA pages (Svelte 5 runes).
 * Call `reload()` from `$effect` when authenticated.
 */
export function createResource<T>(fallback: T | null = null) {
  let data = $state<T | null>(fallback);
  let error = $state<string | null>(null);
  let status = $state<LoadStatus>("idle");
  let hasLoaded = $state(false);
  let generation = 0;

  async function load(loader: () => Promise<T>): Promise<T | null> {
    const gen = ++generation;
    status = "loading";
    error = null;
    try {
      const next = await loader();
      if (gen !== generation) return data;
      data = next;
      status = "ready";
      hasLoaded = true;
      return next;
    } catch (err) {
      if (gen !== generation) return data;
      error = userErrorMessage(err);
      status = "error";
      return null;
    }
  }

  return {
    get data() {
      return data;
    },
    get error() {
      return error;
    },
    get status() {
      return status;
    },
    /** True only before the first successful load — keeps list/detail content mounted during refresh. */
    get loading() {
      return !hasLoaded && (status === "idle" || status === "loading");
    },
    /** True while re-fetching after data was already shown once. */
    get refreshing() {
      return hasLoaded && status === "loading";
    },
    get ready() {
      return status === "ready";
    },
    setData(next: T | null) {
      data = next;
      status = next === null ? "idle" : "ready";
      error = null;
      if (next !== null) hasLoaded = true;
    },
    load,
  };
}
